import {
  Alert,
  Autocomplete,
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import DataExport from "../components/DataExport";
import DataImport from "../components/DataImport";
import { useSettings } from "../contexts/SettingsContext";
import { currencies, Currency } from "../data/currencies";
import { Settings } from "../types/settings";

export default function SettingsPage() {
  const { settings: contextSettings, updateSettings } = useSettings();
  const [settings, setSettings] = useState<Settings>(contextSettings);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  useEffect(() => {
    setSettings(contextSettings);
  }, [contextSettings]);

  const handleSettingChange = (
    field: keyof Settings,
    value: string | number
  ) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    updateSettings(newSettings);
    setSnackbar({ open: true, message: "Settings saved successfully" });
  };

  const getCurrentCurrency = (): Currency | null => {
    return (
      currencies.find(
        (currency) => currency.symbol === settings.currencySymbol
      ) || null
    );
  };

  return (
    <Container maxWidth="sm" sx={{ paddingX: 4, paddingY: 6 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Currency Settings
        </Typography>
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexDirection: "column" }}>
          <Autocomplete
            value={getCurrentCurrency()}
            onChange={(_, newValue: Currency | null) => {
              if (newValue) {
                handleSettingChange("currencySymbol", newValue.symbol);
              }
            }}
            options={currencies}
            getOptionLabel={(option) =>
              `${option.name} (${option.code}) ${option.symbol}`
            }
            renderInput={(params) => (
              <TextField {...params} label="Select Currency" />
            )}
            isOptionEqualToValue={(option, value) =>
              option.symbol === value.symbol
            }
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Decimal Places</InputLabel>
            <Select
              value={settings.currencyRounding}
              label="Decimal Places"
              onChange={(e) =>
                handleSettingChange("currencyRounding", Number(e.target.value))
              }>
              <MenuItem value={0}>0 (1234)</MenuItem>
              <MenuItem value={1}>1 (123.4)</MenuItem>
              <MenuItem value={2}>2 (12.34)</MenuItem>
              <MenuItem value={3}>3 (1.234)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <DataExport />

      <DataImport />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity="success"
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
